import { logClientError } from 'src/util/server-log';
import { Inject, Injectable } from "@angular/core";
import { APP_BASE_HREF } from "@angular/common";
import { ORGANIZATION_ID } from "@models/organization";
import { SocialAuthProvider } from "@models/user";
import * as AuthService from "@proto/auth/auth";
import { SharedService } from "@services/shared/shared.service";
import { AuthorizationInterceptor } from "src/app/interceptors/interceptor";
import { environment } from "src/environments/environment";
import { endpointBaseGRPC } from "src/main";

@Injectable({
  providedIn: "root",
})
export class GrpcAuthService {
  private clientAuth: AuthService.auth.v1.AuthServiceClient;

  constructor(
    private sharedService: SharedService,
    private authInterceptor: AuthorizationInterceptor,
    @Inject(APP_BASE_HREF) private baseHref: string,
  ) {
    this.clientAuth = new AuthService.auth.v1.AuthServiceClient(endpointBaseGRPC + "/auth", undefined, {
      unaryInterceptors: [this.authInterceptor],
    });
  }

  signUp(
    email: string,
    firstName: string,
    lastName: string,
    phoneNumber: string,
    password: string,
  ): Promise<AuthService.auth.v1.SignUpResponse> {
    const request = new AuthService.auth.v1.SignUpRequest({
      organizationId: ORGANIZATION_ID,
      firstName,
      lastName,
      phoneNumber,
      username: email,
      email,
      password,
    });
    return this.clientAuth.SignUp(request, {});
  }

  // confirmationSignUp(email: string, code: string): Promise<AuthService.auth.v1.ValidateCodeOAuthResponse> {
  //   const request = new AuthService.auth.v1.ValidateCodeOAuthRequest({
  //     code,
  //     state: "",
  //   });
  //   return this.clientAuth.ValidateCodeOAuth(request, {});
  // }

  login(username: string, password: string): Promise<AuthService.auth.v1.LoginResponse> {
    const passwordAuth = new AuthService.auth.v1.PasswordAuth({
      username,
      password,
    });
    const request = new AuthService.auth.v1.LoginRequest({
      passwordAuth,
    });
    return this.clientAuth.Login(request, {});
  }

  async getAndSetCurrentUser(accessToken?: string, redirectOnError = true): Promise<void> {
    if (accessToken) {
      this.sharedService.accessToken = accessToken;
    }

    if (!this.sharedService.accessToken) {
      return;
    }

    try {
      const request = new AuthService.auth.v1.GetAuthUserRequest({
        userId: this.sharedService.user?.id || this.sharedService.user?.sub,
      });
      const response = await this.clientAuth.GetAuthUser(request, {});
      const res = response.toObject();
      const user = {
        ...this.sharedService.user,
        ...res,
        name: `${res.firstName} ${res.lastName}`,
        given_name: res.firstName,
        family_name: res.lastName,
        preferred_username: `${res.firstName} ${res.lastName}`,
      };

      this.sharedService.user = user;
      this.sharedService.user$.next(user);
    } catch (error) {
      logClientError("Error fetching user data:", error);
      if (redirectOnError) this.sharedService.logoutUser();
      throw error;
    }
  }

  loginWithoutPassword(email: string): Promise<AuthService.auth.v1.LoginResponse> {
    const request = new AuthService.auth.v1.LoginRequest({
      passwordlessAuth: new AuthService.auth.v1.PasswordlessAuth({
        identifier: email,
      }),
    });
    return this.clientAuth.Login(request, {});
  }

  loginWithProvider(provider: SocialAuthProvider): Promise<AuthService.auth.v1.LoginOAuthResponse> {
    const request = new AuthService.auth.v1.LoginOAuthRequest({
      organizationId: ORGANIZATION_ID,
      redirectUri: window.location.origin + this.baseHref + 'entrar',
      scope: environment.grpc.scope,
      provider,
      codeChallengeMethod: AuthService.auth.v1.CodeChallengeMethod.CODE_CHALLENGE_METHOD_S256,
    });
    return this.clientAuth.LoginOAuth(request, {});
  }

  validateCodeOAuth(code: string, state: string): Promise<AuthService.auth.v1.ValidateCodeOAuthResponse> {
    const request = new AuthService.auth.v1.ValidateCodeOAuthRequest({
      code,
      state,
    });
    return this.clientAuth.ValidateCodeOAuth(request, {});
  }



  changeUserProfile(
    firstName?: string,
    lastName?: string,
    phoneNumber?: string,
  ): Promise<AuthService.auth.v1.ChangeUserProfileResponse> {
    const request = new AuthService.auth.v1.ChangeUserProfileRequest({
      userId: this.sharedService.user?.id || this.sharedService.user?.sub,
      firstName,
      lastName,
      phoneNumber,
    });
    return this.clientAuth.ChangeProfile(request, {});
  }

  startEmailChange(newEmail: string): Promise<AuthService.auth.v1.StartEmailChangeResponse> {
    const request = new AuthService.auth.v1.StartEmailChangeRequest({
      userId: this.sharedService.user?.id || this.sharedService.user?.sub,
      newEmail,
    });
    return this.clientAuth.StartEmailChange(request, {});
  }

  async verifyEmailChange(challenge: string, username: string): Promise<AuthService.auth.v1.VerifyEmailChangeResponse> {
    const request = new AuthService.auth.v1.VerifyEmailChangeRequest({
      sessionId: this.sharedService.mfaChallenge?.mfaSessionId,
      challenge,
      username,
    });
    const response = await this.clientAuth.VerifyEmailChange(request, {});
    const res = response.toObject();

    // Atualiza o sessionId no SharedService se o backend retornar um novo
    if (res.sessionId) {
      this.sharedService.mfaChallenge = {
        ...this.sharedService.mfaChallenge,
        mfaSessionId: res.sessionId,
      };
    }

    // Se tiver errorMessage, lança erro para ser tratado pelo chamador
    if (res.errorMessage) {
      throw new Error(res.errorMessage);
    }

    return response;
  }

  startPasswordChange(newPassword: string): Promise<AuthService.auth.v1.StartPasswordChangeResponse> {
    const request = new AuthService.auth.v1.StartPasswordChangeRequest({
      userId: this.sharedService.user?.id || this.sharedService.user?.sub,
      newPassword,
    });
    return this.clientAuth.StartPasswordChange(request, {});
  }

  async verifyPasswordChange(challenge: string, username: string): Promise<AuthService.auth.v1.VerifyPasswordChangeResponse> {
    const request = new AuthService.auth.v1.VerifyPasswordChangeRequest({
      sessionId: this.sharedService.mfaChallenge?.mfaSessionId,
      challenge,
      username,
    });
    const response = await this.clientAuth.VerifyPasswordChange(request, {});
    const res = response.toObject();

    // Atualiza o sessionId no SharedService se o backend retornar um novo
    if (res.sessionId) {
      this.sharedService.mfaChallenge = {
        ...this.sharedService.mfaChallenge,
        mfaSessionId: res.sessionId,
      };
    }

    // Se tiver errorMessage, lança erro para ser tratado pelo chamador
    if (res.errorMessage) {
      throw new Error(res.errorMessage);
    }

    return response;
  }

  deleteAccount(reason?: string, subjectId?: string): Promise<AuthService.auth.v1.DeleteUserResponse> {
    const request = new AuthService.auth.v1.DeleteUserRequest({
      reason,
      subjectId,
    });
    return this.clientAuth.DeleteUser(request, {});
  }

  async resendConfirmationCode(
    method: AuthService.auth.v1.MethodValidationCode,
    username: string,
  ): Promise<AuthService.auth.v1.ResendValidationCodeChallengeResponse> {
    const request = new AuthService.auth.v1.ResendValidationCodeChallengeRequest({
      sessionId: this.sharedService.mfaChallenge?.mfaSessionId,
      method,
      username,
    });
    const response = await this.clientAuth.ResendValidationCodeChallenge(request, {});
    const res = response.toObject();

    // Atualiza o sessionId no SharedService se o backend retornar um novo
    if (res.sessionId) {
      this.sharedService.mfaChallenge = {
        ...this.sharedService.mfaChallenge,
        mfaSessionId: res.sessionId,
      };
    }

    return response;
  }

  loginPasswordless(email: string): Promise<AuthService.auth.v1.LoginResponse> {
    const passwordlessAuth = new AuthService.auth.v1.PasswordlessAuth({
      identifier: email,
    });
    const request = new AuthService.auth.v1.LoginRequest({
      passwordlessAuth,
    });
    return this.clientAuth.Login(request, {});
  }

  refreshToken(): Promise<AuthService.auth.v1.RefreshTokenResponse> {
    if (!this.sharedService.refreshToken) {
      throw new Error("Refresh token not found");
    }
    const request = new AuthService.auth.v1.RefreshTokenRequest({
      refreshToken: this.sharedService.refreshToken,
    });
    return this.clientAuth.RefreshToken(request, {});
  }

  forgotPassword(email?: string): Promise<AuthService.auth.v1.ForgotMyPasswordResponse> {
    const requestObj: any = {};
    if (email) {
      requestObj.email = email;
    }
    const request = new AuthService.auth.v1.ForgotMyPasswordRequest(requestObj);
    return this.clientAuth.ForgotMyPassword(request, {});
  }

  resetPassword(code: string, newPassword: string): Promise<AuthService.auth.v1.ResetMyPasswordResponse> {
    const sessionId = this.sharedService.mfaChallenge?.mfaSessionId ?? "";
    const request = new AuthService.auth.v1.ResetMyPasswordRequest({
      sessionId,
      code,
      newPassword,
    });
    return this.clientAuth.ResetMyPassword(request, {});
  }

  /**
   * Define nova senha para usuário que foi criado com senha temporária.
   * Usado no cenário de force-new-password após login.
   * Não requer código OTP, apenas o mfaSessionId do MFAChallenge como session.
   */
  setNewPassword(session: string, newPassword: string): Promise<AuthService.auth.v1.ConfirmPasswordChangeResponse> {
    const request = new AuthService.auth.v1.ConfirmPasswordChangeRequest({
      session,
      newPassword,
    });
    return this.clientAuth.ConfirmPasswordChange(request, {});
  }

  logout(): Promise<AuthService.auth.v1.LogoutResponse> {
    const request = new AuthService.auth.v1.LogoutRequest({
      refreshToken: this.sharedService.refreshToken,
    });
    return this.clientAuth.Logout(request, {});
  }

  updateUserAddress(address: {
    zipCode: string;
    street: string;
    number: string;
    complement?: string;
    neighborhood: string;
    city: string;
    state: string;
    country?: string;
  }): Promise<AuthService.auth.v1.UpdateAuthUserResponse> {
    const addressProto = new AuthService.auth.v1.Address({
      zipCode: address.zipCode,
      street: address.street,
      number: address.number,
      complement: address.complement || "",
      neighborhood: address.neighborhood,
      city: address.city,
      state: address.state,
      country: address.country || "BR",
    });
    const request = new AuthService.auth.v1.UpdateAuthUserRequest({
      userId: this.sharedService.user?.id || this.sharedService.user?.sub,
      address: addressProto,
    });
    return this.clientAuth.UpdateAuthUser(request, {});
  }

  async validateMFA(
    mfaSessionId: string,
    code: string,
    method: AuthService.auth.v1.MethodValidationCode,
    username: string,
  ): Promise<AuthService.auth.v1.ValidateMFAResponse> {
    const request = new AuthService.auth.v1.ValidateMFARequest({
      mfaSessionId,
      code,
      method,
      username,
    });
    const response = await this.clientAuth.ValidateMFA(request, {});
    const res = response.toObject();

    // Se retornar um novo mfaChallenge com sessionId, atualiza no SharedService
    if (res.mfaChallenge?.mfaSessionId) {
      this.sharedService.mfaChallenge = {
        ...this.sharedService.mfaChallenge,
        mfaSessionId: res.mfaChallenge.mfaSessionId,
        mfaMethods: res.mfaChallenge.mfaMethods || this.sharedService.mfaChallenge?.mfaMethods,
      };
    }

    return response;
  }
}
